import { log, logV } from "../../../shared/services/debug-logger";
import { VESSEL_CARD_ZN_QUERY_CONDITION } from "../constants/vessel-card-zn.constants";
import { getVesselCardMockResponse } from "../mocks/vessel-card.mocks";
import type {
  VesselCardZnLoadResult,
  VesselCardZnScenario,
  VesselCardZnScenarioSelection,
  VesselCardZnRequest,
} from "../models/vessel-card-zn.models";
import type { VesselCardPerformanceRow } from "../models/vessel-card.models";
import { getVesselCardZnModel, getVesselCardZnMockModel } from "../services/vessel-card-zn.service";
import { vesselCardMockEnabledAtom } from "../state/vessel-card.atoms";
import { getVesselCardZnModelState, toVesselCardZnRows } from "../utils/vessel-card-zn.utils";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";

export const useVesselCardZn = (imo: string, performance: VesselCardPerformanceRow[]) => {
  const useMockData = useAtomValue(vesselCardMockEnabledAtom);
  const [selection, setSelection] = useState<VesselCardZnScenarioSelection>({ imo, scenario: "available" });
  const scenario = selection.imo === imo ? selection.scenario : "available";
  const [result, setResult] = useState<VesselCardZnLoadResult | null>(null);
  const expectedInputs = JSON.stringify(
    performance.map((row) => ({
      mode: row.mode,
      speed: row.expectedSpeed,
      expectedConsumption: row.expectedConsumption,
    })),
  );
  const request = useMemo<VesselCardZnRequest>(() => {
    const fixture = getVesselCardMockResponse(imo).card;
    return {
      imo,
      summerDwt: fixture.vessel.imo === imo ? fixture.summerDwt ?? null : null,
      condition: VESSEL_CARD_ZN_QUERY_CONDITION,
      points: JSON.parse(expectedInputs) as VesselCardZnRequest["points"],
    };
  }, [imo, expectedInputs]);
  const mockRequest = useMockData ? request : null;
  // CP edits never reload stored ZN points. Expected-consumption-only changes recalculate the delta locally.
  const key = JSON.stringify({
    imo,
    useMockData,
    scenario: useMockData ? scenario : null,
    points: useMockData ? request.points : request.points.map(({ mode, speed }) => ({ mode, speed })),
  });

  useEffect(() => {
    const controller = new AbortController();
    log("STATE - useVesselCardZn - load", { source: mockRequest ? "mock" : "snowflake" });
    const load = mockRequest
      ? getVesselCardZnMockModel(mockRequest, scenario, controller.signal)
      : getVesselCardZnModel(imo, controller.signal);
    void load
      .then((response) => {
        if (!controller.signal.aborted) {
          log("STATE - useVesselCardZn - apply", { modelAvailable: response.modelAvailable });
          setResult({ key, response, error: false });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          log("STATE - useVesselCardZn - failed");
          setResult({ key, response: null, error: true });
        }
      });
    return () => {
      logV("STATE - useVesselCardZn - DISPOSE");
      controller.abort();
    };
  }, [mockRequest, scenario, key, imo]);

  // Keying by IMO and expected inputs hides stale predictions immediately, before the next effect runs.
  const current = result?.key === key ? result : null;
  const response = current?.response?.imo === imo ? current.response : null;
  const modelState = getVesselCardZnModelState(response);
  return {
    scenario,
    useMockData,
    response,
    setScenario: (next: VesselCardZnScenario) => setSelection({ imo, scenario: next }),
    loading: current === null,
    error: current?.error ?? false,
    modelState,
    trainedAt: response?.trainedAt ?? null,
    rows: toVesselCardZnRows(performance, response, modelState),
  };
};
